import useReveal from "@/hooks/useReveal";
import { Check } from "lucide-react";

const reasons = [
  "One connected platform for the entire clinic workflow",
  "Less admin after every appointment",
  "Faster note completion with AI-assisted documentation",
  "Cleaner, more organised patient records",
  "Smoother workflow from consultation to invoice",
  "Designed specifically for modern private clinics",
];

/* Product-focused visual for the Why section */
const WhyVisual = () => (
  <div className="rounded-3xl overflow-hidden bg-[#131E1E] p-6 md:p-8" data-testid="why-section-image">
    {/* Dashboard widgets */}
    <div className="grid grid-cols-2 gap-3 md:gap-4 mb-4">
      <div className="bg-white/[0.06] rounded-xl p-4 border border-white/[0.06]">
        <p className="text-[10px] text-white/40 uppercase tracking-wider mb-2 font-medium">This week</p>
        <p className="text-2xl font-medium text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>42</p>
        <p className="text-[11px] text-white/50">appointments completed</p>
      </div>
      <div className="bg-white/[0.06] rounded-xl p-4 border border-white/[0.06]">
        <p className="text-[10px] text-white/40 uppercase tracking-wider mb-2 font-medium">AI notes</p>
        <p className="text-2xl font-medium text-[#7EBDBC]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>38</p>
        <p className="text-[11px] text-white/50">notes approved & saved</p>
      </div>
    </div>
    {/* Workflow timeline */}
    <div className="bg-white/[0.04] rounded-xl p-4 border border-white/[0.06] mb-4">
      <p className="text-[10px] text-white/40 uppercase tracking-wider mb-3 font-medium">Recent workflow</p>
      <div className="space-y-3">
        {[
          { action: "Note approved", patient: "M. Rivera", time: "2 min ago", status: "done" },
          { action: "Invoice generated", patient: "S. Thompson", time: "8 min ago", status: "done" },
          { action: "Consultation recorded", patient: "L. Chen", time: "15 min ago", status: "active" },
        ].map((item, i) => (
          <div key={i} className="flex items-center gap-3">
            <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${item.status === "active" ? "bg-[#7EBDBC]" : "bg-white/20"}`} />
            <div className="flex-1 min-w-0">
              <span className="text-[11px] text-white/80 font-medium">{item.action}</span>
              <span className="text-[11px] text-white/30 mx-1.5">·</span>
              <span className="text-[11px] text-white/40">{item.patient}</span>
            </div>
            <span className="text-[10px] text-white/25 flex-shrink-0">{item.time}</span>
          </div>
        ))}
      </div>
    </div>
    {/* Status bar */}
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <div className="w-2 h-2 rounded-full bg-[#205B5A]" />
        <span className="text-[10px] text-white/40">All systems operational</span>
      </div>
      <span className="text-[10px] text-white/25">LumeroFlow v2.4</span>
    </div>
  </div>
);

const WhyLumeroFlowSection = () => {
  const [ref, isVisible] = useReveal();

  return (
    <section
      ref={ref}
      id="why-lumeroflow"
      data-testid="why-lumeroflow-section"
      className="py-20 md:py-32"
      style={{ backgroundColor: "#FAF9F6" }}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left: Product Visual */}
          <div className={`reveal-section ${isVisible ? "visible" : ""}`}>
            <WhyVisual />
          </div>

          {/* Right: Content */}
          <div className={`reveal-section ${isVisible ? "visible" : ""}`} style={{ transitionDelay: "0.15s" }}>
            <p className="text-sm font-medium uppercase tracking-[0.2em] text-[#205B5A] mb-5">
              Why LumeroFlow
            </p>
            <h2
              data-testid="why-heading"
              className="text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight leading-tight text-[#1A1B1A] mb-10"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              Why clinics choose LumeroFlow
            </h2>
            <div className="space-y-5">
              {reasons.map((reason, i) => (
                <div
                  key={i}
                  data-testid={`why-reason-${i}`}
                  className="flex items-start gap-4"
                >
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#205B5A]/10 flex items-center justify-center mt-0.5">
                    <Check className="w-3.5 h-3.5 text-[#205B5A]" strokeWidth={2.5} />
                  </div>
                  <p className="text-base md:text-lg text-[#1A1B1A] leading-relaxed font-normal">
                    {reason}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyLumeroFlowSection;
